import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import SubjectFilterBar from '../../components/feature/SubjectFilterBar';
import SubjectManagerModal from '../../components/feature/SubjectManagerModal';
import { supabase, StudyMaterial } from '../../lib/supabase';
import { useFavorites } from '../../hooks/useFavorites';
import MaterialForm from './components/MaterialForm';
import { useSubjectList } from '../../hooks/useSubjectList';
import { updatePageSEO, createBreadcrumbSchema, createCollectionPageSchema, cleanupDynamicSchemas } from '../../utils/seo';
import { toggleMaterialLike } from '../../hooks/useNotifications';

export default function MaterialsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { subjects: subjectTags, refreshSubjects } = useSubjectList();
  const { isFavorited, toggleFavorite } = useFavorites('material');
  const [materials, setMaterials] = useState<StudyMaterial[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [viewMode, setViewMode] = useState<'all' | 'mine'>('all');
  const [filterMode, setFilterMode] = useState<'all' | 'favorites'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<StudyMaterial | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showSubjectManager, setShowSubjectManager] = useState(false);
  // いいね状態管理
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [likingIds, setLikingIds] = useState<Set<string>>(new Set());
  // ハイライト対象（通知からの遷移）
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  // いいね数・自分のいいね状態を一括取得
  const fetchLikes = useCallback(async (materialIds: string[]) => {
    if (materialIds.length === 0) return;
    try {
      const { data: likesData } = await supabase
        .from('material_likes')
        .select('material_id')
        .in('material_id', materialIds);

      const counts: Record<string, number> = {};
      materialIds.forEach(id => { counts[id] = 0; });
      (likesData || []).forEach((l: { material_id: string }) => {
        counts[l.material_id] = (counts[l.material_id] || 0) + 1;
      });
      setLikeCounts(counts);

      if (user) {
        const { data: myLikes } = await supabase
          .from('material_likes')
          .select('material_id')
          .in('material_id', materialIds)
          .eq('user_id', user.id);
        const myLikedSet = new Set<string>((myLikes || []).map((l: { material_id: string }) => l.material_id));
        setLikedIds(myLikedSet);
      }
    } catch (e) {
      console.error('いいね取得エラー:', e);
    }
  }, [user]);

  const fetchMaterials = useCallback(async () => {
    setMaterialsLoading(true);
    let query = supabase
      .from('study_materials')
      .select('*');
    
    if (viewMode === 'mine' && user) {
      query = query.eq('user_id', user.id);
    } else if (viewMode === 'all') {
      query = query.eq('is_public', true);
    }
    
    if (selectedSubject) query = query.eq('subject', selectedSubject);
    query = query.order('created_at', { ascending: sortOrder === 'oldest' });
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching study materials:', error);
      setMaterials([]);
      setMaterialsLoading(false);
      return;
    }

    const materialsData = data || [];

    const userIds = [...new Set(materialsData.map((m: any) => m.user_id).filter(Boolean))];
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url')
        .in('id', userIds);

      const profileMap: Record<string, any> = {};
      (profiles || []).forEach((p: any) => { profileMap[p.id] = p; });

      const merged = materialsData.map((m: any) => ({
        ...m,
        user_profiles: m.user_id ? (profileMap[m.user_id] || null) : null,
      }));
      setMaterials(merged);
      // いいね情報を取得
      await fetchLikes(merged.map((m: any) => m.id));
    } else {
      setMaterials(materialsData);
    }
    
    setMaterialsLoading(false);
  }, [selectedSubject, sortOrder, viewMode, user, fetchLikes]);

  useEffect(() => {
    updatePageSEO({
      title: '勉強資料｜永山の勉強アーカイブ - 学習内容まとめ・復習資料',
      description: '永山の勉強アーカイブの勉強資料ページ。学習した内容を科目別にまとめた復習資料を公開しています。',
      keywords: '勉強資料,学習資料,復習資料,永山の勉強アーカイブ,学習記録',
      canonical: 'https://nagayama-study-archive.readdy.ai/materials',
      ogType: 'website',
    });
    createBreadcrumbSchema([
      { name: 'ホーム', url: 'https://nagayama-study-archive.readdy.ai/' },
      { name: '勉強資料', url: 'https://nagayama-study-archive.readdy.ai/materials' },
    ]);
    createCollectionPageSchema({
      name: '勉強資料一覧',
      description: '学習した内容を科目別にまとめた復習資料の一覧ページ。',
      url: 'https://nagayama-study-archive.readdy.ai/materials',
    });
    return () => { cleanupDynamicSchemas(); };
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // URLパラメータ highlight 処理：該当資料をハイライト＆スクロール
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (!highlight || materialsLoading) return;
    setHighlightId(highlight);
    // URLパラメータをクリア
    setSearchParams({}, { replace: true });
    // スクロールは少し遅延させてDOMが描画されてから
    setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, materialsLoading]);

  // ハイライトは3秒後に消す
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightId]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleMaterialLike = async (e: React.MouseEvent, material: StudyMaterial) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || likingIds.has(material.id)) return;
    const ownerId = (material as any).user_id;
    if (!ownerId) return;
    setLikingIds(prev => new Set(prev).add(material.id));
    try {
      const result = await toggleMaterialLike(material.id, material.title, ownerId, user.id);
      setLikeCounts(prev => ({ ...prev, [material.id]: result.count }));
      setLikedIds(prev => {
        const next = new Set(prev);
        if (result.liked) next.add(material.id); else next.delete(material.id);
        return next;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLikingIds(prev => { const next = new Set(prev); next.delete(material.id); return next; });
    }
  };

  // ローディング中（ページ全体）
  if (materialsLoading && materials.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  // 未ログイン時のガード表示
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header />
        <section className="pt-20 sm:pt-24 pb-10 sm:pb-12 hero-gradient-animated hero-dark-force relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/30">
                <i className="ri-folder-line text-4xl text-white"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">学習資料</h1>
              <p className="text-lg text-teal-100">学習に必要な資料を一元管理します</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/login')} className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg">
                <i className="ri-login-box-line mr-2"></i>ログイン
              </button>
              <button onClick={() => navigate('/login')} className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm">
                <i className="ri-user-add-line mr-2"></i>新規登録
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase.from('study_materials').delete().eq('id', id).eq('user_id', user!.id);
      if (error) throw error;
      setConfirmDeleteId(null);
      setToast({ message: '資料を削除しました', type: 'success' });
      fetchMaterials();
    } catch {
      setToast({ message: '削除に失敗しました', type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return 'ri-file-text-line';
    if (fileType.includes('pdf')) return 'ri-file-pdf-line';
    if (fileType.includes('word') || fileType.includes('document')) return 'ri-file-word-line';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'ri-file-excel-line';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'ri-file-ppt-line';
    return 'ri-file-text-line';
  };

  const getPreviewUrl = (url: string) => {
    if (url.includes('drive.google.com')) {
      const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (fileIdMatch) return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
    }
    return url;
  };

  const isMyMaterial = (m: StudyMaterial) => {
    if (!user) return false;
    const materialUserId = (m as StudyMaterial & { user_id?: string }).user_id;
    return materialUserId === user.id;
  };

  const filteredMaterials = filterMode === 'favorites' 
    ? materials.filter(m => isFavorited(m.id))
    : materials;

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900">
      <Header />

      {toast && (
        <div className="fixed top-4 sm:top-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in px-4 w-full max-w-md">
          <div className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl shadow-lg text-xs sm:text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-sm sm:text-base`}></i>
            <span className="break-words">{toast.message}</span>
          </div>
        </div>
      )}

      <section className="pt-20 sm:pt-24 pb-10 sm:pb-12 hero-gradient-animated hero-dark-force relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-orb-1 absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-2 absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-3 absolute top-1/3 left-1/3 w-[350px] h-[350px] bg-teal-200/8 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-40 -right-40 w-[800px] h-[200px] bg-gradient-to-r from-transparent via-white/5 to-transparent rotate-[30deg] blur-2xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-teal-200 mb-4 sm:mb-6 animate-hero-fade-up">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">Top</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">勉強資料</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 animate-hero-fade-up-delay">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-tight">勉強資料</h1>
              <p className="text-teal-100 text-xs sm:text-sm md:text-base max-w-2xl leading-relaxed">
                学習した内容を復習しやすいように自分なりにまとめたものです。科目やテーマで絞り込むことができます。
              </p>
            </div>
            <div className="flex items-center gap-2 animate-hero-fade-up-delay2">
              <button
                onClick={() => setShowSubjectManager(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white/15 text-white rounded-xl text-xs sm:text-sm font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm border border-white/10 shadow-sm min-h-[40px] sm:min-h-[44px]"
              >
                <i className="ri-folder-settings-line text-sm sm:text-base"></i>
                <span className="hidden sm:inline">科目・カテゴリ管理</span>
                <span className="sm:hidden">管理</span>
              </button>
              {user && (
                <button
                  onClick={() => { setEditingMaterial(null); setShowForm(true); }}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white/15 text-white rounded-xl text-xs sm:text-sm font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm border border-white/10 shadow-sm min-h-[40px] sm:min-h-[44px]"
                >
                  <i className="ri-add-line text-base sm:text-lg"></i>
                  <span className="hidden sm:inline">資料を追加</span>
                  <span className="sm:hidden">追加</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {user && (
        <FadeInSection>
          <section className="py-3 sm:py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                  <button
                    onClick={() => setViewMode('all')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${viewMode === 'all' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-global-line mr-1 sm:mr-1.5"></i>全体
                  </button>
                  <button
                    onClick={() => setViewMode('mine')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${viewMode === 'mine' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-user-line mr-1 sm:mr-1.5"></i>自分の資料
                  </button>
                </div>

                <div className="h-5 sm:h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

                <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${filterMode === 'all' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-file-list-line mr-1 sm:mr-1.5"></i>すべて
                  </button>
                  <button
                    onClick={() => setFilterMode('favorites')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${filterMode === 'favorites' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-heart-fill mr-1 sm:mr-1.5"></i>お気に入り
                  </button>
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>
      )}

      <FadeInSection>
        <section className="py-4 sm:py-6 md:py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <SubjectFilterBar
                  subjects={subjectTags}
                  selectedSubject={selectedSubject}
                  onSelectSubject={setSelectedSubject}
                  onRefresh={refreshSubjects}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">並び替え</label>
                <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white dark:bg-gray-700 dark:text-white min-h-[40px]">
                  <option value="newest">新着順</option>
                  <option value="oldest">古い順</option>
                </select>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      <FadeInSection>
        <section className="py-8 sm:py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {materialsLoading ? (
              <div className="text-center py-12 sm:py-16"><div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div></div>
            ) : filteredMaterials.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-4 sm:mb-6">
                  <i className={`${filterMode === 'favorites' ? 'ri-heart-line' : 'ri-file-pdf-line'} text-3xl sm:text-4xl text-gray-400 dark:text-gray-500`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  {filterMode === 'favorites' ? 'お気に入りの資料がありません' : viewMode === 'mine' ? 'まだ自分の資料がありません' : 'まだ資料がありません'}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 px-4">
                  {filterMode === 'favorites' ? 'ハートアイコンをクリックして資料をお気に入りに追加しましょう。' : viewMode === 'mine' ? '「資料を追加」ボタンから最初の資料を登録しましょう。' : '勉強資料が投稿されると、ここに表示されます。'}
                </p>
                {viewMode === 'mine' && user && filterMode !== 'favorites' && (
                  <button
                    onClick={() => { setEditingMaterial(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm sm:text-base font-medium hover:from-teal-600 hover:to-emerald-700 transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40 min-h-[44px]"
                  >
                    <i className="ri-add-line"></i>最初の資料を追加
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {filteredMaterials.map((material) => {
                  const isLiked = likedIds.has(material.id);
                  const likeCount = likeCounts[material.id] || 0;
                  const isLiking = likingIds.has(material.id);
                  const isHighlighted = highlightId === material.id;

                  return (
                    <div
                      key={material.id}
                      ref={isHighlighted ? highlightRef : null}
                      className={`bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl border p-4 sm:p-6 hover:-translate-y-1 transition-all duration-300 shadow-sm flex flex-col ${
                        isHighlighted
                          ? 'border-teal-400 ring-2 ring-teal-400/50 shadow-teal-200/60 shadow-lg animate-pulse-once'
                          : 'border-gray-200 dark:border-gray-700 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                          <span className="px-2 sm:px-2.5 py-0.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full whitespace-nowrap">{material.subject}</span>
                          {isMyMaterial(material) && (
                            <span className="px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-full whitespace-nowrap">自分</span>
                          )}
                          {isMyMaterial(material) && (
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full flex items-center gap-0.5 whitespace-nowrap ${(material as StudyMaterial & { is_public?: boolean }).is_public !== false ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'}`}>
                              <i className={`${(material as StudyMaterial & { is_public?: boolean }).is_public !== false ? 'ri-global-line' : 'ri-lock-line'} text-xs`}></i>
                              {(material as StudyMaterial & { is_public?: boolean }).is_public !== false ? '公開' : '非公開'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {user && (
                            <button
                              onClick={() => toggleFavorite(material.id)}
                              className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl transition-all ${
                                isFavorited(material.id)
                                  ? 'bg-red-50 dark:bg-red-900/30 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50'
                                  : 'bg-gray-50 dark:bg-gray-700 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-red-500'
                              }`}
                              title={isFavorited(material.id) ? 'お気に入りから削除' : 'お気に入りに追加'}
                            >
                              <i className={`${isFavorited(material.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-base sm:text-lg`}></i>
                            </button>
                          )}
                          <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 rounded-xl">
                            <i className={`${getFileIcon(material.file_type)} text-base sm:text-lg text-teal-600 dark:text-teal-400`}></i>
                          </div>
                        </div>
                      </div>
                      
                      {(material as any).user_profiles && (
                        <div className="flex items-center gap-2 mb-3">
                          {(material as any).user_profiles.avatar_url ? (
                            <img src={(material as any).user_profiles.avatar_url} alt={(material as any).user_profiles.username} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white text-xs font-semibold">
                              {(material as any).user_profiles.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {(material as any).user_profiles.username}
                          </span>
                        </div>
                      )}
                      
                      <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2 line-clamp-2 break-words">{material.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 line-clamp-3 leading-relaxed break-words">{material.description}</p>
                      
                      {/* カード下部 */}
                      <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(material.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* いいねボタン */}
                          <button
                            onClick={(e) => handleMaterialLike(e, material)}
                            disabled={!user || isLiking}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer disabled:opacity-50 ${
                              isLiked
                                ? 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                                : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400'
                            }`}
                            title={user ? (isLiked ? 'いいねを取り消す' : 'いいねする') : 'ログインが必要です'}
                          >
                            <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                            <span>{likeCount}</span>
                          </button>

                          {viewMode === 'mine' && isMyMaterial(material) && (
                            <>
                              <button
                                onClick={() => { setEditingMaterial(material); setShowForm(true); }}
                                className="p-1.5 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
                                title="編集"
                              >
                                <i className="ri-edit-line text-sm"></i>
                              </button>
                              {confirmDeleteId === material.id ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer whitespace-nowrap min-h-[32px]">いいえ</button>
                                  <button
                                    onClick={() => handleDelete(material.id)}
                                    disabled={deletingId === material.id}
                                    className="px-2 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded cursor-pointer whitespace-nowrap disabled:opacity-50 min-h-[32px]"
                                  >
                                    {deletingId === material.id ? '...' : 'はい'}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setConfirmDeleteId(material.id)}
                                  className="p-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
                                  title="削除"
                                >
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              )}
                            </>
                          )}
                          {material.file_url && (
                            <button
                              onClick={() => setSelectedMaterial(material)}
                              className="text-xs sm:text-sm text-teal-600 dark:text-teal-400 font-medium hover:text-teal-700 dark:hover:text-teal-300 cursor-pointer whitespace-nowrap min-h-[32px] px-2"
                            >
                              閲覧 →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </FadeInSection>

      {selectedMaterial && selectedMaterial.file_url && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setSelectedMaterial(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-5xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sm:hidden flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <span className="px-2.5 py-0.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full whitespace-nowrap">{selectedMaterial.subject}</span>
                <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white truncate">{selectedMaterial.title}</h3>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                <a href={selectedMaterial.file_url} target="_blank" rel="noopener noreferrer" className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors cursor-pointer whitespace-nowrap">開く</a>
                <button onClick={() => setSelectedMaterial(null)} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"><i className="ri-close-line text-lg sm:text-xl text-gray-600 dark:text-gray-400"></i></button>
              </div>
            </div>
            <div className="flex-1 p-3 sm:p-4 overflow-hidden">
              <iframe src={getPreviewUrl(selectedMaterial.file_url)} className="w-full h-full min-h-[60vh] sm:min-h-[70vh] rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700" title={selectedMaterial.title} allow="autoplay" />
            </div>
            {selectedMaterial.description && (
              <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{selectedMaterial.description}</p></div>
            )}
          </div>
        </div>
      )}

      {showForm && user && (
        <MaterialForm
          userId={user.id}
          material={editingMaterial}
          onSaved={() => {
            setShowForm(false);
            setEditingMaterial(null);
            setToast({ message: editingMaterial ? '資料を更新しました' : '資料を追加しました', type: 'success' });
            fetchMaterials();
            refreshSubjects();
          }}
          onClose={() => { setShowForm(false); setEditingMaterial(null); }}
        />
      )}

      {showSubjectManager && (
        <SubjectManagerModal
          onClose={() => setShowSubjectManager(false)}
          onUpdated={() => {
            refreshSubjects();
          }}
        />
      )}

      <Footer />
    </div>
  );
}
